import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { LogIn, AlertCircle, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const StockTicker = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    const fetchPrice = () => {
      fetch(`${API}/api/stock-price`).then(r => r.json()).then(setData).catch(() => {});
    };
    fetchPrice();
    const timer = setInterval(fetchPrice, 60000);
    return () => clearInterval(timer);
  }, []);

  if (!data) return null;

  const renderExchange = (label, info) => {
    if (!info) return null;
    const isUp = info.change > 0;
    const isDown = info.change < 0;
    return (
      <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }}>
        <span className="text-xs font-semibold tracking-wider opacity-60" style={{ minWidth: '28px' }}>{label}</span>
        <span className="text-lg font-bold text-white font-mono">₹{info.price?.toFixed(2)}</span>
        {info.change != null && (
          <span className={`flex items-center gap-0.5 text-xs font-semibold ${isUp ? 'text-emerald-400' : isDown ? 'text-red-400' : 'text-slate-400'}`}>
            {isUp ? <TrendingUp className="w-3 h-3" /> : isDown ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
            {isUp ? '+' : ''}{info.change?.toFixed(2)} ({isUp ? '+' : ''}{info.pct?.toFixed(2)}%)
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-1.5">
      {renderExchange('NSE', data.nse)}
      {renderExchange('BSE', data.bse)}
      {data.updated && (
        <p className="text-xs opacity-40 pl-4 pt-1">Last updated: {data.updated}</p>
      )}
    </div>
  );
};

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Sharat Industries Stitch Branding */}
      <div className="hidden lg:flex lg:w-3/5 relative flex-col overflow-hidden">
        <iframe
          src="/api/brand-panel"
          title="Sharat Industries"
          className="w-full h-full border-0"
          style={{ minHeight: '100vh' }}
        />
        {/* Stock ticker overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4 z-10"
             style={{ background: 'linear-gradient(transparent, rgba(11,44,74,0.95) 40%)' }}>
          <p className="text-[10px] tracking-[0.15em] uppercase text-white/40 mb-2 pl-1">SHINDL · Live Share Price</p>
          <StockTicker />
          <p className="text-white/20 text-xs mt-3 pl-1">© 2024 Sharat Industries Limited · Blue Economy Excellence</p>
        </div>
      </div>
      
      {/* Right side - Login form */}
      <div className="flex-1 lg:w-2/5 flex items-center justify-center p-8 bg-white">
        <Card className="w-full max-w-md border-zinc-200">
          <CardHeader className="space-y-1">
            <CardTitle className="text-3xl font-bold tracking-tight text-zinc-950">Welcome back</CardTitle>
            <CardDescription className="text-zinc-500">
              Sign in to your SIL Insights account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 text-red-600 bg-red-50 border border-red-200 rounded-md text-sm" data-testid="login-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-zinc-950 font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="border-zinc-200 focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                  data-testid="login-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-zinc-950 font-medium">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="border-zinc-200 focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                  data-testid="login-password-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-blue-700 hover:bg-blue-800 text-white"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="w-4 h-4" />
                    Sign In
                  </span>
                )}
              </Button>
            </form>
            
            <div className="mt-6 text-center text-sm text-zinc-500">
              Don't have an account?{' '}
              <Link to="/register" className="text-blue-700 hover:text-blue-800 font-medium" data-testid="register-link">
                Create account
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
